import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AggregatorRegionDto } from '@shared/utils/interfaces';
import { hideLoading, showLoading, withFeatureLoading } from '@shared/utils/models';
import { catchError, defer, EMPTY, exhaustMap, finalize, iif, map, Observable, of, pipe, tap } from 'rxjs';

import { RegionsApiService } from '../../services';
import { RegionsState } from './regions.interface';

export const INITIAL_STATE: RegionsState = {
  regions: [],
  aggregatorRegions: [],
};

export const RegionsStore = signalStore(
  {
    providedIn: 'root',
  },

  withState(INITIAL_STATE),

  withFeatureLoading(),

  withComputed(({ regions }) => ({
    regionsDropdownOptions: computed(() => [
      ...regions().map((region) => ({
        text: region.name,
        value: region.id,
      })),
    ]),
  })),

  withMethods((store, regionsApiService = inject(RegionsApiService)) => ({
    getAggregatorRegions(): Observable<AggregatorRegionDto[]> {
      if (store.aggregatorRegions().length) {
        return of(store.aggregatorRegions());
      }

      patchState(store, showLoading());

      return regionsApiService.getAggregatorRegions().pipe(
        map(({ payload }) => payload),
        tap((payload) => patchState(store, { aggregatorRegions: payload })),
        finalize(() => patchState(store, hideLoading())),
      );
    },
  })),

  withMethods((store, regionsApiService = inject(RegionsApiService)) => ({
    loadRegions: rxMethod<void>(
      pipe(
        exhaustMap(() =>
          iif(
            () => Boolean(store.regions().length),
            of(null),
            defer(() => {
              patchState(store, showLoading());

              return regionsApiService.getRegions().pipe(
                tapResponse({
                  next: (regions) => {
                    patchState(store, { regions: regions.payload });
                  },
                  error: (error) => {
                    console.error(error);
                  },
                }),
                finalize(() => patchState(store, hideLoading())),
              );
            }),
          ),
        ),
      ),
    ),

    loadAggregatorRegions: rxMethod<void>(
      pipe(
        exhaustMap(() =>
          store.getAggregatorRegions().pipe(
            catchError((error) => {
              console.error(error);

              return EMPTY;
            }),
          ),
        ),
      ),
    ),
  })),
);
